import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { Section } from "@/components/Section";
import { Button } from "@/components/ui/button";

const SERVICES = [
  {
    name: "Google Business Profile & local SEO",
    detail:
      "Review eligibility, categories, services, opening hours and contact details. Align your website and genuine business listings; make it straightforward for customers to leave honest reviews.",
  },
  {
    name: "Technical & on-page SEO",
    detail:
      "Check crawling, indexing, duplicate pages and mobile performance. Map each service to a useful page with clear headings, internal links and an enquiry path.",
  },
  {
    name: "Bangla & English search planning",
    detail:
      "Compare actual queries in Search Console with your services. Plan Bengali and English pages where they help customers, rather than translating every keyword into duplicate pages.",
  },
  {
    name: "Measurement & enquiry tracking",
    detail:
      "Establish a baseline for search impressions, clicks and enquiries. Separate completed work from observed changes, and agree which calls or forms can be measured before reporting leads.",
  },
];

export function RangpurSeoDetails() {
  return (
    <>
      <Section eyebrow="Scope of work" title="SEO services for your Rangpur business">
        <div className="grid gap-8 md:grid-cols-2">
          {SERVICES.map((service) => (
            <article key={service.name} className="border-t border-border pt-6">
              <h3 className="font-display text-xl">{service.name}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">{service.detail}</p>
            </article>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild variant="outline">
            <Link to="/pricing">
              Packages & pricing <ArrowUpRight />
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/book">
              Discuss your project <ArrowUpRight />
            </Link>
          </Button>
        </div>
      </Section>

      <Section eyebrow="Local planning" title="Match the page to the customer’s question">
        <p className="max-w-3xl text-muted-foreground">
          These are illustrative planning examples—not measured keyword volumes or client results.
          The right targets depend on your actual location, services and search data.
        </p>
        <div className="mt-8 grid gap-8 md:grid-cols-3">
          <article className="border-t border-border pt-6">
            <h3 className="font-display text-xl">Clinics around Dhap & Medical Mor</h3>
            <p className="mt-3 text-muted-foreground">
              A patient needs the available service, clinician information, appointment process and
              directions. Use accurate medical information reviewed by the clinic; avoid promising
              treatment outcomes.
            </p>
          </article>
          <article className="border-t border-border pt-6">
            <h3 className="font-display text-xl">Retail near Jahaj Company More</h3>
            <p className="mt-3 text-muted-foreground">
              A shopper needs to know what you sell, where to find the shop and how to check
              availability. Connect useful product or category pages with consistent location
              details.
            </p>
          </article>
          <article className="border-t border-border pt-6">
            <h3 className="font-display text-xl">Coaching & education services</h3>
            <p className="mt-3 text-muted-foreground">
              Families need course details, admission requirements, fees and the real campus
              address. Build pages around those decisions instead of repeating a list of Rangpur
              neighborhoods.
            </p>
          </article>
        </div>
      </Section>

      <Section eyebrow="Before you hire" title="A clear scope, not a ranking guarantee">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h3 className="font-display text-2xl">What we agree before work starts</h3>
            <ul className="mt-6 space-y-4">
              {[
                "Your website, real service area, priority services and enquiry goal.",
                "A baseline review of Search Console and your Business Profile, with access granted securely—not shared passwords.",
                "A written list of priorities, deliverables, responsibilities and exclusions.",
                "A review schedule that distinguishes work delivered from changes Google actually reports.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-muted-foreground">
                  <Check size={18} className="mt-1 shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-2xl">Check the work and its limits</h3>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              I’m Rahatul Ashiq Tamal, working as Freelancer Tamal from Rangpur. Review my project
              examples and their evidence notes before hiring. They show work context where
              available; they do not establish a verified Rangpur traffic or revenue uplift.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
              <Link to="/about" className="text-primary underline underline-offset-4">
                About Tamal
              </Link>
              <Link to="/case-studies" className="text-primary underline underline-offset-4">
                Project examples
              </Link>
              <Link to="/proof" className="text-primary underline underline-offset-4">
                Evidence & limitations
              </Link>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Google describes local rankings in terms of relevance, distance and prominence.
              Profile improvements cannot change a searcher’s location or guarantee first place.{" "}
              <a
                href="https://support.google.com/business/answer/7091"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4"
              >
                Google’s local ranking guidance
              </a>
              .
            </p>
          </div>
        </div>
      </Section>
    </>
  );
}
